/* =============================================================================
   LA Header — custom element definitions
   Loaded by sections/la-header.liquid (defer).

   Classes defined here:
     LaStickyHeader  → <la-sticky-header> — manages show/hide on scroll
     LaMegaMenu      → <la-mega-menu>     — hover mega panel + category switching
   ============================================================================= */

/* =============================================================================
   LaStickyHeader
   Mirrors Dawn's StickyHeader behaviour but operates on <la-sticky-header>
   so it doesn't conflict with the removed Dawn header section.

   Dawn class names (shopify-section-header-sticky / hidden / animate) are
   preserved so base.css scroll-compensation rules continue to work.
   ============================================================================= */
class LaStickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this; // the custom element IS the outer sticky wrapper

    this.headerIsAlwaysSticky = this.dataset.stickyType === 'always';
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.headerBounds = { top: 0, bottom: 0 };

    // Re-measure on resize (e.g. mobile orientation change)
    window.matchMedia('(max-width: 989px)').addEventListener(
      'change',
      this._measureHeaderGroup.bind(this)
    );

    this._onScrollBound = this._onScroll.bind(this);
    window.addEventListener('scroll', this._onScrollBound, { passive: true });

    // Allow other components (e.g. search modal) to prevent the header revealing
    this.addEventListener('preventHeaderReveal', () => {
      this.preventReveal = true;
    });

    this._measureHeaderGroup();
  }

  _measureHeaderGroup() {
    // Measure announcement bar sections above this header in the header group.
    let announcementH = 0;
    const mySection = this.closest('.shopify-section-group-header-group');
    if (mySection) {
      let el = mySection.previousElementSibling;
      while (el) {
        if (el.classList.contains('shopify-section-group-header-group')) {
          announcementH += el.offsetHeight;
        }
        el = el.previousElementSibling;
      }
    }

    this._announcementH = announcementH;

    // --header-height = just the nav header bar (gallery sticks below it once it's at top: 0)
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);

    // Hide/reveal triggers after scrolling past announcement bar + nav header
    this.headerBounds = { top: 0, bottom: announcementH + this.offsetHeight };

    // Set initial top position (header sits below announcement bar at rest)
    this._applyTop();
  }

  // Slide the header's top from announcementH → 0 as the announcement bar scrolls away
  _applyTop(scrollTop = window.pageYOffset || document.documentElement.scrollTop) {
    this.style.top = `${Math.max(0, this._announcementH - scrollTop)}px`;
  }

  _onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Keep header top position in sync as announcement bar scrolls away
    this._applyTop(scrollTop);

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      // Scrolling down past the header — hide it
      if (!this.preventHide) requestAnimationFrame(this._hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      // Scrolling up while past the header position — reveal it
      if (!this.preventReveal) {
        requestAnimationFrame(this._reveal.bind(this));
      } else {
        clearTimeout(this._isScrolling);
        this._isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this._hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      // Back near the top — full reset
      requestAnimationFrame(this._reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  _hide() {
    if (this.headerIsAlwaysSticky) return;
    this.classList.add('animate', 'shopify-section-header-hidden');
    this.dispatchEvent(new CustomEvent('headerIsHidden', { bubbles: true }));
  }

  _reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.classList.add('animate');
    this.classList.remove('shopify-section-header-hidden');
    this.dispatchEvent(new CustomEvent('headerIsRevealed', { bubbles: true }));
  }

  _reset() {
    if (this.headerIsAlwaysSticky) return;
    // Back near top — show header instantly (no animation)
    this.classList.remove('shopify-section-header-hidden', 'animate');
    this.dispatchEvent(new CustomEvent('headerIsReset', { bubbles: true }));
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScrollBound);
  }
}

customElements.define('la-sticky-header', LaStickyHeader);

/* =============================================================================
   LaMegaMenu
   Manages the desktop hover mega panel and category-switching.

   DOM expectations:
     - #la-shop-trigger   → the "Shop" <a> in the desktop nav
     - .la-mega-menu__panel  → the drop panel (direct child of this element)
     - .la-category-item  → category buttons in the panel (data-category attr)
     - .la-mega-menu__content-panel → one per category (data-category attr)
   ============================================================================= */
class LaMegaMenu extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.la-mega-menu__panel');
    this.categories = this.querySelectorAll('.la-category-item');
    // The trigger lives outside this element, in the desktop nav
    this.trigger = document.querySelector('#la-shop-trigger');

    this._closeDelay = null;

    if (!this.panel || !this.trigger) return;

    this._bindEvents();

    // Activate the first category by default so content is ready on first open
    const firstCat = this.categories[0];
    if (firstCat) this._activateCategory(firstCat.dataset.category, false);
  }

  _bindEvents() {
    // Open on trigger hover or keyboard focus
    this.trigger.addEventListener('mouseenter', () => this._openPanel());
    this.trigger.addEventListener('focus', () => this._openPanel());

    // Trigger mouseleave starts the close countdown
    this.trigger.addEventListener('mouseleave', () => {
      this._scheduleClose();
    });

    // Panel mouseenter cancels any pending close
    this.panel.addEventListener('mouseenter', () => {
      clearTimeout(this._closeDelay);
    });

    // Panel mouseleave closes
    this.panel.addEventListener('mouseleave', () => {
      this._closePanel();
    });

    // Category hover switches visible content
    this.categories.forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        this._activateCategory(btn.dataset.category);
      });
      btn.addEventListener('focus', () => {
        this._activateCategory(btn.dataset.category);
      });
      btn.addEventListener('keydown', (e) => this._onCategoryKeydown(e));
    });

    // ESC closes and returns focus to trigger
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.hasAttribute('data-open')) {
        this._closePanel();
        this.trigger.focus();
      }
    });

    // Click outside the panel and trigger closes it (touch-device support)
    document.addEventListener('click', (e) => {
      if (
        this.hasAttribute('data-open') &&
        !this.contains(e.target) &&
        !this.trigger.contains(e.target)
      ) {
        this._closePanel();
      }
    });

    // Close when sticky header fires its hide event
    const stickyHeader = document.querySelector('la-sticky-header');
    if (stickyHeader) {
      stickyHeader.addEventListener('headerIsHidden', () => this._closePanel());
    }
  }

  _openPanel() {
    clearTimeout(this._closeDelay);
    this.setAttribute('data-open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  _closePanel() {
    clearTimeout(this._closeDelay);
    this.removeAttribute('data-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  // A short delay allows the pointer to travel from the trigger to the panel
  // without the panel vanishing in between.
  _scheduleClose() {
    this._closeDelay = setTimeout(() => this._closePanel(), 280);
  }

  _activateCategory(handle, animate = true) {
    if (!handle) return;

    // Swap active class on category buttons
    this.categories.forEach((btn) => {
      const isThis = btn.dataset.category === handle;
      btn.classList.toggle('is-active', isThis);
      btn.setAttribute('aria-expanded', isThis ? 'true' : 'false');
    });

    // Swap visible content panel
    this.querySelectorAll('.la-mega-menu__content-panel').forEach((panel) => {
      const isThis = panel.dataset.category === handle;
      panel.classList.toggle('is-active', isThis);
      // aria-hidden keeps screen readers from reading off-screen content
      panel.setAttribute('aria-hidden', isThis ? 'false' : 'true');
    });
  }

  // Arrow key navigation between category items
  _onCategoryKeydown(e) {
    const items = Array.from(this.categories);
    const idx = items.indexOf(e.currentTarget);
    if (e.code === 'ArrowDown') {
      e.preventDefault();
      items[Math.min(idx + 1, items.length - 1)]?.focus();
    }
    if (e.code === 'ArrowUp') {
      e.preventDefault();
      items[Math.max(idx - 1, 0)]?.focus();
    }
  }

  disconnectedCallback() {
    clearTimeout(this._closeDelay);
  }
}

customElements.define('la-mega-menu', LaMegaMenu);
